import type { ImageScenario } from '../game/imageTypes';

/**
 * Scenario 02 -- a Jira "Map statuses to columns" board configuration screen,
 * annotated with ten red arrows.
 *
 * ============================ DRAFT CLASSIFICATION ==========================
 * The arrow REGIONS are measured from the image and should be correct.
 *
 * Which arrows are real defects and which are decoys is MY reading of the
 * screenshot, not a statement of intent -- whoever drew the arrows knows what
 * they meant. Move an entry between `defects` and a plain decoy to re-classify
 * it; nothing else needs to change.
 * ============================================================================
 *
 * Image is 2000 x 877. All regions are percentages of those dimensions.
 */
export const boardConfig: ImageScenario = {
  id: 'jira-board-config',
  title: 'Map statuses to columns',
  subtitle: 'Kanban board configuration',
  brief:
    'This is the column configuration behind a Kanban board. Ten red arrows point at things somebody thought were worth a second look. Some of them are genuinely misconfigured. The rest are perfectly fine — your job is to tell which is which.',

  source: {
    toolId: 'jira',
    screen: 'Board settings › Columns ("Map statuses to columns")',
    kind: 'screenshot',
    note: 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  },

  image: {
    src: '/scenarios/board-config.webp',
    width: 2000,
    height: 877,
    alt: 'Jira board settings screen titled "Map statuses to columns", showing Unmapped statuses, Kanban backlog, and the columns Backlog, Selected for Development, In Progress and Done, each with Min and Max fields set to None.',
  },

  arrows: [
    {
      id: 'add-status',
      label: '1',
      region: { x: 5.95, y: 29.19, w: 3.00, h: 13.45 },
      description: 'Arrow pointing at the Add status button under Unmapped statuses',
      decoyNote:
        'Unmapped statuses is empty, which is the healthy state — it means no workflow status is invisible to the board. The "Add status" button is just the control for creating one; its presence is not a finding.',
    },
    {
      id: 'kanban-backlog',
      label: '2',
      region: { x: 22.10, y: 31.24, w: 3.20, h: 18.02 },
      description: 'Arrow pointing at the empty Kanban backlog panel',
    },
    {
      id: 'backlog-column',
      label: '3',
      region: { x: 37.40, y: 2.28, w: 3.10, h: 10.26 },
      description: 'Arrow pointing at the BACKLOG column header',
    },
    {
      id: 'sfd-max-wip',
      label: '4',
      region: { x: 58.05, y: 12.77, w: 3.00, h: 9.58 },
      description:
        'Arrow pointing at the Max field of the Selected for Development column, set to None',
    },
    {
      id: 'sfd-set-resolution',
      label: '5',
      region: { x: 46.40, y: 34.18, w: 4.40, h: 6.00 },
      description:
        'Arrow pointing at the unchecked Set resolution box on Selected for Development',
      decoyNote:
        'Set resolution is correctly unchecked here. Resolution should only be stamped when work reaches a terminal state; setting it on a queue column would mark items resolved while they are still waiting to be started.',
    },
    {
      id: 'sfd-status-lozenge',
      label: '6',
      region: { x: 59.70, y: 29.19, w: 4.90, h: 6.16 },
      description:
        'Arrow pointing at the "Selected for Development" status inside its column',
      decoyNote:
        'The status is mapped to the column of the same name, holding one work item. That is exactly the intended one-to-one mapping; nothing here is misconfigured.',
    },
    {
      id: 'inprogress-set-resolution',
      label: '7',
      region: { x: 68.60, y: 36.94, w: 3.60, h: 15.74 },
      description: 'Arrow pointing at the unchecked Set resolution box on In Progress',
      decoyNote:
        'Correctly unchecked. In Progress is not a terminal state — stamping a resolution here would make active work register as resolved in every report and filter.',
    },
    {
      id: 'done-column-header',
      label: '8',
      region: { x: 84.60, y: 1.82, w: 3.20, h: 7.75 },
      description: 'Arrow pointing at the DONE column header with its green bar',
      decoyNote:
        'The green bar and the tick icon confirm Done is registered as the board’s completion column. This is what a correctly configured final column looks like.',
    },
    {
      id: 'add-column',
      label: '9',
      region: { x: 94.60, y: 14.37, w: 3.00, h: 10.49 },
      description: 'Arrow pointing at the plus button for adding a new column',
      decoyNote:
        'This is just the "add a column" control. An unused button is not a misconfiguration.',
    },
    {
      id: 'done-set-resolution',
      label: '10',
      region: { x: 84.95, y: 41.96, w: 3.00, h: 11.86 },
      description: 'Arrow pointing at the checked Set resolution box on Done',
      decoyNote:
        'Checked, and correctly so. Done is the terminal column, and this is precisely where a resolution should be stamped. Unchecking it would leave completed work permanently unresolved.',
    },
  ],

  defects: [
    {
      id: 'd-backlog-as-column',
      arrowId: 'backlog-column',
      title: 'Backlog status occupies a board column',
      severity: 'major',
      explanation:
        'The Backlog status is mapped to a column on the board itself, and it holds 6 of the board’s 7 work items. Every un-started item is therefore permanently on the board, so the board stops showing "work in flight" and starts showing "everything anyone has ever asked for". The signal a Kanban board exists to give — what is moving, and what is stuck — is drowned out.',
      fix: 'Drag the Backlog status into the Kanban backlog panel on the left. The items stay accessible for planning but leave the board, so the columns show only work that has actually been started.',
    },
    {
      id: 'd-kanban-backlog-unused',
      arrowId: 'kanban-backlog',
      title: 'Kanban backlog never enabled',
      severity: 'major',
      explanation:
        'The panel still reads "Drag and drop a status here to enable the Kanban backlog", so the feature is switched off. This is the other half of the previous problem: because no status was mapped here, un-started work had nowhere to live except a board column. The team also loses backlog-only tools such as bulk ranking and estimation.',
      fix: 'Map the Backlog status to this panel, which both enables the backlog and clears the board in one action.',
    },
    {
      id: 'd-no-wip-limits',
      arrowId: 'sfd-max-wip',
      title: 'No WIP limits on any column',
      severity: 'major',
      explanation:
        'Every Min and Max on this screen reads None, so no column has a work-in-progress limit — and the arrow points at the one that matters most, the queue feeding development. Kanban without WIP limits is just a board with columns: nothing ever forces the team to finish work before starting more, and nothing turns red when a queue silently grows.',
      fix: 'Set a Max on the flow columns — commonly around 1.5× the number of people who can work an item. Start with In Progress and Selected for Development, and tune from what actually turns red.',
    },
  ],
};
